import { Check, ExternalLink, Newspaper, Radio } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { Reveal, ParallaxImage } from "../components/Motion";
import { PageHeader, CtaBand } from "../components/Detail";
import { WhatsAppButton } from "../components/Chrome";
import { PRESS } from "../data/press";
import { IMAGES } from "../lib/site";

const PressSection = () => {
  const { t } = useLanguage();
  const p = t.about.press;
  return (
    <section className="mt-16" data-testid="press-section">
      <Reveal>
        <p className="text-[11px] font-bold uppercase tracking-[0.28em] text-teal-dark">{p.title}</p>
        <h2 className="mt-3 font-display text-2xl font-semibold tracking-tight text-petrol sm:text-3xl">{p.sub}</h2>
        <span className="mt-4 block h-0.5 w-10 rounded-full bg-gold" aria-hidden="true" />
      </Reveal>
      <div className="mt-8 grid gap-4 md:grid-cols-3">
        {PRESS.map((item, i) => (
          <Reveal key={item.id} delay={i * 0.06}>
            {item.image ? (
              <a
                href={item.image}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`press-card-${item.id}`}
                className="group flex h-full flex-col overflow-hidden rounded-3xl border border-warmline bg-white transition-all hover:-translate-y-1 hover:border-teal"
              >
                <div className="flex aspect-[4/3] items-center justify-center overflow-hidden bg-cream p-3">
                  <img
                    src={item.image}
                    alt={`${item.outlet} – ${p.articleLabel}`}
                    className="max-h-full max-w-full rounded-xl object-contain shadow-sm"
                    loading="lazy"
                  />
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-teal-light px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-teal-dark">
                    <Newspaper className="h-3 w-3" aria-hidden="true" />
                    {p.articleLabel}
                  </span>
                  <p className="mt-3 font-display text-lg font-semibold text-petrol">{item.outlet}</p>
                  <p className="text-sm text-slate2">{item.date}</p>
                  <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-teal-dark">
                    {p.viewCta}
                    <ExternalLink className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                  </span>
                </div>
              </a>
            ) : (
              <a
                href={item.link}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`press-card-${item.id}`}
                className="group flex h-full flex-col justify-between rounded-3xl bg-petrol p-6 transition-all hover:-translate-y-1"
              >
                <div>
                  <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-teal-bright">
                    <Radio className="h-3 w-3" aria-hidden="true" />
                    {p.radioLabel}
                  </span>
                  <p className="mt-4 font-display text-xl font-semibold text-white">{item.outlet}</p>
                  <p className="mt-1 text-sm text-white/65">{item.date}</p>
                </div>
                <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-bold text-teal-bright">
                  {p.viewCta}
                  <ExternalLink className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                </span>
              </a>
            )}
          </Reveal>
        ))}
      </div>
    </section>
  );
};

export default function About() {
  const { t } = useLanguage();
  const a = t.about;
  return (
    <main id="main-content">
      <PageHeader overline={a.pageOverline} title={a.pageTitle} sub={a.pageSub} overlineTestId="about-page-overline" />

      <div className="mx-auto max-w-7xl px-5 py-14 sm:px-8 sm:py-20 lg:px-12">
        <div className="grid items-start gap-10 lg:grid-cols-[0.85fr_1.15fr]">
          <Reveal>
            <img
              src={IMAGES.hafencity}
              alt="Naciye Rafiq-Arli, Heilpraktikerin für Psychotherapie, vor der Hamburger HafenCity"
              className="aspect-[4/3] w-full rounded-[2.5rem] object-cover shadow-lg shadow-petrol/10"
              data-testid="about-portrait"
            />
          </Reveal>
          <div>
            <Reveal>
              <p className="text-lg leading-relaxed text-slate2" data-testid="about-intro">
                {a.intro}
              </p>
            </Reveal>
            <Reveal delay={0.06}>
              <dl className="mt-8 divide-y divide-warmline border-y border-warmline" data-testid="about-facts">
                {a.facts.map((f, i) => (
                  <div key={i} className="grid grid-cols-[7.5rem_1fr] gap-4 py-3.5 sm:grid-cols-[11rem_1fr]">
                    <dt className="text-[11px] font-bold uppercase tracking-[0.15em] text-gold-dark">{f.label}</dt>
                    <dd className="text-sm font-medium text-ink sm:text-base">{f.value}</dd>
                  </div>
                ))}
              </dl>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="mt-7">
                <WhatsAppButton label={t.common.whatsappCtaLong} testId="about-whatsapp-cta" className="px-6 py-3 text-sm" />
              </div>
            </Reveal>
          </div>
        </div>

        <div className="mt-14 grid gap-4 lg:grid-cols-2">
          <Reveal>
            <article className="h-full rounded-3xl bg-petrol p-8 sm:p-10" data-testid="about-approach-card">
              <h2 className="font-display text-2xl font-semibold tracking-tight text-white sm:text-3xl">{a.approach.title}</h2>
              <p className="mt-4 leading-relaxed text-white/75">{a.approach.text}</p>
            </article>
          </Reveal>
          <Reveal delay={0.06}>
            <article className="h-full rounded-3xl border border-warmline bg-white p-8 sm:p-10" data-testid="about-helplist-card">
              <h2 className="font-display text-2xl font-semibold tracking-tight text-petrol sm:text-3xl">{a.helpList.title}</h2>
              <ul className="mt-5 space-y-3">
                {a.helpList.items.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-slate2 sm:text-base">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-teal-light">
                      <Check className="h-3 w-3 text-teal-dark" aria-hidden="true" />
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 border-t border-warmline pt-5 text-sm leading-relaxed text-slate2" data-testid="about-engagement">
                {a.engagement.text}
              </p>
            </article>
          </Reveal>
        </div>

        <PressSection />

        <section className="mt-16">
          <Reveal>
            <p className="text-[11px] font-bold uppercase tracking-[0.28em] text-teal-dark">{a.gallery.title}</p>
          </Reveal>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <ParallaxImage src={IMAGES.room} alt={a.gallery.alts.room} className="aspect-[4/3] rounded-3xl" speed={26} />
            <ParallaxImage src={IMAGES.hafencity} alt={a.gallery.alts.hafencity} className="aspect-[4/3] rounded-3xl sm:mt-8" speed={40} />
            <ParallaxImage src={IMAGES.certificates} alt={a.gallery.alts.certificates} className="aspect-[4/3] rounded-3xl sm:col-span-2 lg:col-span-1 lg:mt-16" speed={54} />
          </div>
        </section>

        <div className="mt-16">
          <CtaBand testId="about-cta-band" ctaTestId="about-cta-whatsapp" />
        </div>
      </div>
    </main>
  );
}
